import { Component, OnInit, ViewChild } from '@angular/core';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { ToastService } from '../../../../services/toast.service';
import { CapturaPeso } from '../../../../types/CapturaPeso.type';
import { TipoAnalise } from '../../../../types/TipoAnalise.type';
import { CapturaPesoFiltro, CapturaPesoResumo, CapturaPesoService, ConfirmarPesagemDto, RemessaPesagemDto } from './capturapeso.service';
import { AnalisePesagemService } from '../../../integracoes/parametrizacao/analisepesagem/analisepesagem.service';

@Component({
  selector: 'app-capturapeso',
  templateUrl: './capturapeso.component.html',
  styleUrl: './capturapeso.component.css'
})
export class CapturaPesoComponent implements OnInit {

  @ViewChild('dt') dt!: Table;

  capturas: CapturaPeso[] = [];
  loading: boolean = false;
  loadingExportar: boolean = false;

  // Paginação server-side
  totalRecords: number = 0;
  rows: number = 20;
  first: number = 0;
  sortField: string = 'remessa';
  sortOrder: number = -1;
  busca: string = '';
  statusRemessaSelecionados: string[] = [];
  produtosSelecionados: string[] = [];

  instanciaSelecionada: string = localStorage.getItem('instancia') ?? '';

  // Contadores por status (endpoint de resumo), respeitando a busca ativa
  resumo: CapturaPesoResumo | null = null;
  private buscaTimeout: any;

  displayDialog: boolean = false;
  displayDetalhesDialog: boolean = false;
  capturaSelecionada: CapturaPeso = {} as CapturaPeso;
  detalhesSelecionado: CapturaPeso = {} as CapturaPeso;

  pesoCapturado: number | null = null;
  tipoProdutoCapturado: string = '';
  dadosPesagem: RemessaPesagemDto | null = null;
  loadingDadosPesagem: boolean = false;
  loadingCapturar: boolean = false;
  loadingSalvar: boolean = false;

  tiposAnalise: TipoAnalise[] = [];
  analises: { codigo_analise: string; valor_analise: number | null }[] = [];
  lacres: string[] = [];
  lacreInput: string = '';

  analisePermitida: boolean = false;
  loadingVerificarAnalise: boolean = false;

  lacreExibir: boolean = false;
  lacreObrigatorio: boolean = false;
  loadingVerificarLacre: boolean = false;

  constructor(
    private capturaPesoService: CapturaPesoService,
    private analisePesagemService: AnalisePesagemService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    // A primeira busca de dados é disparada pelo (onLazyLoad) da tabela.
    this.carregarResumo();
    this.carregarTiposAnalise();
  }

  carregarTiposAnalise(): void {
    if (!this.instanciaSelecionada) {
      this.tiposAnalise = [];
      return;
    }
    this.analisePesagemService.findAtivos(this.instanciaSelecionada).subscribe({
      next: (data) => { this.tiposAnalise = data; },
      error: () => { this.tiposAnalise = []; }
    });
  }

  onInstanciaChange(): void {
    localStorage.setItem('instancia', this.instanciaSelecionada);
    this.first = 0;
    if (this.dt) {
      this.dt.first = 0;
    }
    this.carregarTiposAnalise();
    this.recarregar();
  }

  carregar(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? this.rows;
    if (event.sortField) {
      this.sortField = event.sortField as string;
      this.sortOrder = event.sortOrder ?? 1;
    }
    this.buscarPagina();
  }

  recarregar(): void {
    this.buscarPagina();
    this.carregarResumo();
  }

  private montarFiltro(): CapturaPesoFiltro {
    return {
      busca: this.busca?.trim() || undefined,
      statusRemessa: this.statusRemessaSelecionados.length ? this.statusRemessaSelecionados : undefined,
      produtos: this.produtosSelecionados.length ? this.produtosSelecionados : undefined
    };
  }

  private buscarPagina(): void {
    if (!this.instanciaSelecionada) {
      this.capturas = [];
      this.totalRecords = 0;
      return;
    }
    this.loading = true;
    const page = Math.floor(this.first / this.rows);
    const sort = `${this.sortField},${this.sortOrder === -1 ? 'desc' : 'asc'}`;
    this.capturaPesoService.buscarPaginado(this.instanciaSelecionada, page, this.rows, sort, this.montarFiltro()).subscribe({
      next: (data) => {
        this.capturas = data.content;
        this.totalRecords = data.totalElements;
      },
      error: (error) => {
        this.loading = false;
        this.toastService.erro('Erro', 'Erro ao buscar pesagens: ' + (error?.error?.message ?? error.message));
      },
      complete: () => { this.loading = false; }
    });
  }

  carregarResumo(): void {
    if (!this.instanciaSelecionada) {
      this.resumo = null;
      return;
    }
    this.capturaPesoService.buscarResumo(this.instanciaSelecionada, this.montarFiltro()).subscribe({
      next: (resumo) => { this.resumo = resumo; },
      error: () => { this.resumo = null; }
    });
  }

  /** Busca server-side por remessa, placa ou status, com debounce. */
  onBuscaChange(): void {
    clearTimeout(this.buscaTimeout);
    this.buscaTimeout = setTimeout(() => {
      this.first = 0;
      if (this.dt) {
        this.dt.first = 0;
      }
      this.recarregar();
    }, 400);
  }

  /** Filtro multisseleção da coluna Status (server-side, via lazy load da tabela). */
  onStatusRemessaChange(valores: string[], filterCallback: (valor: any) => void): void {
    this.statusRemessaSelecionados = valores ?? [];
    // O filterCallback atualiza o estado do p-columnFilter e dispara o (onLazyLoad), que recarrega a página.
    filterCallback(this.statusRemessaSelecionados.length ? this.statusRemessaSelecionados : null);
    this.carregarResumo();
  }

  /** Filtro multisseleção da coluna Produto (server-side, via lazy load da tabela). */
  onProdutoChange(valores: string[], filterCallback: (valor: any) => void): void {
    this.produtosSelecionados = valores ?? [];
    filterCallback(this.produtosSelecionados.length ? this.produtosSelecionados : null);
    this.carregarResumo();
  }

  limparFiltros(): void {
    this.busca = '';
    this.statusRemessaSelecionados = [];
    this.produtosSelecionados = [];
    this.limparFiltroTabela('statusRemessa');
    this.limparFiltroTabela('descricaoProduto');
    this.onBuscaChange();
  }

  /** Zera o valor guardado pelo p-columnFilter para que o ícone de filtro deixe de ficar destacado. */
  private limparFiltroTabela(campo: string): void {
    const constraint = this.dt?.filters?.[campo];
    if (Array.isArray(constraint)) {
      constraint.forEach(c => c.value = null);
    } else if (constraint) {
      constraint.value = null;
    }
  }

  // Coluna Tipo: exibida apenas como cor; a legenda abaixo da tabela explica cada uma.
  readonly legendaTipoRemessa = [
    { label: 'Entrada', cor: 'bg-orange-500' },
    { label: 'Saída', cor: 'bg-blue-500' }
  ];

  labelTipoRemessa(codigo: string): string {
    const map: Record<string, string> = { 'E': 'Entrada', 'S': 'Saída' };
    return map[codigo] || codigo;
  }

  corTipoRemessa(codigo: string): string {
    if (!codigo) return '';
    const map: Record<string, string> = { 'E': 'bg-orange-500', 'S': 'bg-blue-500' };
    return map[codigo] || 'bg-gray-400';
  }

  readonly statusRemessaMap: Record<string, string> = {
    'PE': 'Pendente', 'LI': 'Liberado', 'CA': 'Cancelado',
    'AC': 'Abastecendo Carga', 'AL': 'Aguardando Lacre',
    'AA': 'Aguardando Análise', 'NC': 'Não Consistente',
    'PC': 'Pesagem Concluída'
  };

  readonly opcoesStatusRemessa = Object.entries(this.statusRemessaMap)
    .map(([value, label]) => ({ value, label }));

  labelStatusRemessa(codigo: string): string {
    return this.statusRemessaMap[codigo] || codigo;
  }

  /** Opções do filtro de Produto: lista de descrições distintas retornada pelo endpoint de resumo. */
  get opcoesProduto(): { value: string; label: string }[] {
    return (this.resumo?.produtos ?? []).map(p => ({ value: p, label: p }));
  }

  labelStatusInteg(codigo: string): string {
    const map: Record<string, string> = {
      'NAO_INTEGRADO': 'Pendente Integração', 'INTEGRADO': 'Integrado',
      'ERRO': 'Erro', 'ESTORNADO': 'Estornado', 'CANCELADO': 'Cancelado'
    };
    return map[codigo] || codigo;
  }

  private contarStatus(...status: string[]): number {
    const contadores = this.resumo?.contadoresStatus ?? {};
    return status.reduce((total, s) => total + (contadores[s] ?? 0), 0);
  }

  get totalInconsistente(): number {
    return this.contarStatus('NC', 'CA');
  }

  get totalLiberado(): number {
    return this.contarStatus('LI');
  }

  get totalEmAndamento(): number {
    return this.contarStatus('AC', 'AL', 'AA');
  }

  get totalFinalizado(): number {
    return this.contarStatus('PC');
  }

  exportar(): void {
    this.loadingExportar = true;
    this.capturaPesoService.exportar(this.instanciaSelecionada).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'captura_peso.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      },
      error: (error) => {
        this.toastService.erro('Exportar', 'Erro ao exportar: ' + (error?.error ?? error));
        this.loadingExportar = false;
      },
      complete: () => { this.loadingExportar = false; }
    });
  }

  abrirPesar(captura: CapturaPeso): void {
    this.capturaSelecionada = { ...captura };
    this.tipoProdutoCapturado = '';
    this.dadosPesagem = null;
    this.analisePermitida = false;
    this.analises = [];

    // Regra: se peso1 já existe e peso2 não, estamos na etapa 2 (saída) — peso deve ser nulo para forçar captura.
    // Se peso2 já existe (reenvio), pré-preenche com peso2.
    if (captura.peso1 != null && captura.peso2 != null) {
      this.pesoCapturado = captura.peso2;
    } else {
      this.pesoCapturado = null;
    }

    // Pré-preenche lacres se já existirem
    this.lacres = captura.lacre ? captura.lacre.split(';').filter(l => l.trim()) : [];
    this.lacreInput = '';
    this.lacreExibir = false;
    this.lacreObrigatorio = false;

    this.displayDialog = true;

    // Busca dados da remessa + tipo de produto parametrizado
    this.loadingDadosPesagem = true;
    this.capturaPesoService.buscarDadosPesagem(captura.remessa).subscribe({
      next: (dto) => {
        this.dadosPesagem = dto;
        this.tipoProdutoCapturado = dto.tipo_produto || '';
      },
      error: () => {
        this.dadosPesagem = null;
      },
      complete: () => { this.loadingDadosPesagem = false; }
    });

    // Verifica se lacre deve ser exibido e se é obrigatório (só relevante na pesagem de saída, quando peso1 já existe)
    if (captura.peso1 != null) {
      this.loadingVerificarLacre = true;
      this.analisePesagemService.exibirLacre(captura.remessa).subscribe({
        next: (exibir) => {
          this.lacreExibir = exibir;
          if (exibir) {
            this.analisePesagemService.obrigatoriedadeLacre(captura.remessa).subscribe({
              next: (obrigatorio) => { this.lacreObrigatorio = obrigatorio; },
              error: () => { this.lacreObrigatorio = false; },
              complete: () => { this.loadingVerificarLacre = false; }
            });
          } else {
            this.lacreObrigatorio = false;
            this.loadingVerificarLacre = false;
          }
        },
        error: () => {
          this.lacreExibir = false;
          this.lacreObrigatorio = false;
          this.loadingVerificarLacre = false;
        }
      });
    }

    this.loadingVerificarAnalise = true;
    this.analisePesagemService.verificarPermitido(captura.remessa).subscribe({
      next: (permitido) => {
        this.analisePermitida = permitido;
        if (permitido) {
          // Pré-preenche valores de análise caso já existam
          this.analises = this.tiposAnalise.map(t => {
            const existente = captura.analises?.find(a => a.codigoAnalise === t.codigo);
            return {
              codigo_analise: t.codigo,
              valor_analise: existente?.valorAnalise ?? null
            };
          });
        }
      },
      error: () => {
        this.analisePermitida = false;
      },
      complete: () => { this.loadingVerificarAnalise = false; }
    });
  }

  capturarDaBalanca(): void {
    this.loadingCapturar = true;
    this.capturaPesoService.capturarPesoRemessa(this.capturaSelecionada.remessa).subscribe({
      next: (response) => {
        this.pesoCapturado = response.peso * 1000;
        this.tipoProdutoCapturado = response.tipo_produto || '';
        this.toastService.sucesso('Balança', `Peso capturado: ${response.peso * 1000} kg`);
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao capturar peso: ' + (error?.error?.message ?? error.message));
        this.loadingCapturar = false;
      },
      complete: () => { this.loadingCapturar = false; }
    });
  }

  get podeEditarPesoManual(): boolean {
    return this.dadosPesagem?.permite_digitacao_manual ?? false;
  }

  get precisaAnalise(): boolean {
    return this.analisePermitida;
  }

  get lacreVisivel(): boolean {
    return this.capturaSelecionada.peso1 != null && this.lacreExibir;
  }

  get etapaPesagem(): string {
    return this.dadosPesagem?.etapa || (this.capturaSelecionada.peso1 != null ? 'Pesagem de Saída' : 'Pesagem de Entrada');
  }

  adicionarLacre(): void {
    const valor = this.lacreInput.trim();
    if (valor && !this.lacres.includes(valor)) {
      this.lacres.push(valor);
    }
    this.lacreInput = '';
  }

  removerLacre(index: number): void {
    this.lacres.splice(index, 1);
  }

  confirmarSalvar(): void {
    if (this.pesoCapturado == null) {
      this.toastService.alerta('Atenção', 'Capture o peso da balança antes de confirmar.');
      return;
    }
    const tipoProdutoLabel = this.tipoProdutoCapturado || 'produto';
    this.confirmationService.confirm({
      message: `Confirmar o peso <b>${this.pesoCapturado} kg</b> para o produto <b>${tipoProdutoLabel}</b>?`,
      header: 'Confirmação de Pesagem',
      icon: 'pi pi-check-circle',
      acceptLabel: 'Confirmar',
      rejectLabel: 'Cancelar',
      accept: () => { this.salvar(); }
    });
  }

  salvar(): void {
    this.loadingSalvar = true;

    const dto: ConfirmarPesagemDto = {
      remessa: this.capturaSelecionada.remessa,
      peso: Math.round(this.pesoCapturado!),
      analise_ativa: this.precisaAnalise,
      numero_lacre: this.lacreVisivel && this.lacres.length > 0 ? this.lacres.join(';') : null,
      analises: this.precisaAnalise
        ? this.analises.filter(a => a.valor_analise !== null)
        : null
    };

    this.capturaPesoService.confirmarPesagem(this.capturaSelecionada.remessa, dto).subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', 'Pesagem confirmada com sucesso.');
        this.displayDialog = false;
        this.recarregar();
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao confirmar pesagem: ' + (error?.error?.message ?? error.message));
        this.loadingSalvar = false;
      },
      complete: () => { this.loadingSalvar = false; }
    });
  }

  abrirDetalhes(captura: CapturaPeso): void {
    this.detalhesSelecionado = { ...captura };
    this.displayDetalhesDialog = true;
  }

  podeRealizar(captura: CapturaPeso): boolean {
    // LI - Liberado: pronta para pesagem
    // AC - Abastecendo Carga: pesagem em andamento
    // AA - Aguardando Análise: análise ainda não preenchida, ação necessária
    // NC - Não Consistente: erro SAP, aguarda correção
    return ['LI', 'AC', 'AL', 'AA', 'NC'].includes(captura.statusRemessa || '');
  }

  getDescricaoAnalise(codigo: string): string {
    return this.tiposAnalise.find(t => t.codigo === codigo)?.descricao || codigo;
  }
}
